export const EXCEL_ANALYST_CHALLENGE_TRACKER_ID = 'excel-analyst-challenge-5-scenarios-practice-bank';

export const EXCEL_ANALYST_CHALLENGE_LENSES = [
  ['business_question', 'Business question'],
  ['source_sheets', 'Source sheets'],
  ['excel_methods', 'Excel methods'],
  ['calculation_plan', 'Calculation plan'],
  ['pivot_or_summary', 'Pivot / summary design'],
  ['evidence', 'Evidence to show'],
  ['decision', 'Business decision'],
  ['caution', 'Caution / limitation'],
];

export const EXCEL_ANALYST_CHALLENGE_ITEMS = [
  {
    id: 'ALDI',
    title: 'ALDI: June promo sales huddle',
    scenario: 'Regional Manager Dawn Fenwick needs to know whether June promo spend actually helped store performance. Use ALDI_Sales and ALDI_Lookup to enrich till rows, calculate revenue and promo revenue, then summarise results by store, region, product category, and promo flag.',
    tables: [{
      columns: ['Workbook sheets', 'Main fields', 'Business pressure'],
      rows: [['ALDI_Sales + ALDI_Lookup', 'StoreCode, ProductCode, UnitsSold, PromoFlag, UnitPrice, Revenue, PromoRevenue', 'Head Office wants a practical read on whether promo activity is worth continuing.']],
    }],
    answerGuide: {
      business_question: 'Did promoted ALDI sales in June generate enough revenue to justify attention, and which stores/products drove the result?',
      source_sheets: 'Use ALDI_Sales as the transaction table and ALDI_Lookup for store location, region, manager, product name, category, and unit price.',
      excel_methods: 'Use VLOOKUP or XLOOKUP for store/product enrichment, IF for PromoRevenue, calculated columns for Revenue, and PivotTables for store, region, category, and promo summaries.',
      calculation_plan: 'Revenue = UnitsSold x UnitPrice. PromoRevenue = IF(PromoFlag="Y", Revenue, 0). Check lookup formulas fill every row and totals reconcile against row-level revenue.',
      pivot_or_summary: 'Build pivots for total revenue by store/region, promo revenue by category, and promo vs non-promo revenue share.',
      evidence: 'Show pivot totals, top/bottom stores, promo revenue total, and at least one screenshot or copied table from the Analysis sheet.',
      decision: 'Recommend whether to continue, narrow, or rethink promo activity based on which stores/categories produced meaningful promo revenue.',
      caution: 'Promo revenue alone does not prove uplift because there is no non-promo baseline or margin view; flag that the analysis is revenue-focused, not causal ROI.',
    },
    ruleTerms: {
      business_question: ['promo', 'revenue', 'store'],
      source_sheets: ['aldi_sales', 'aldi_lookup'],
      excel_methods: ['vlookup', 'xlookup', 'if', 'pivot'],
      calculation_plan: ['units', 'price', 'promorevenue'],
      pivot_or_summary: ['store', 'region', 'category'],
      evidence: ['pivot', 'total'],
      decision: ['continue', 'narrow', 'rethink'],
      caution: ['baseline', 'margin', 'causal'],
    },
    criticalTerms: { source_sheets: ['ALDI_Sales'], calculation_plan: ['PromoRevenue'] },
  },
  {
    id: 'AFRISHOP',
    title: "AfriShop: Busy tills, weak profit",
    scenario: "Owner Kunta Osei says the shop looks busy but the account balance does not match the activity. Use AfriShop_Sales and AfriShop_Lookup to calculate revenue, cost, profit, and margin by product, category, supplier, payment type, and branch.",
    tables: [{
      columns: ['Workbook sheets', 'Main fields', 'Business pressure'],
      rows: [['AfriShop_Sales + AfriShop_Lookup', 'ProductCode, QtySold, Branch, PaymentType, UnitCost, UnitPrice, Revenue, Profit', 'The owner must decide whether prices, supplier costs, or product mix are hurting profit.']],
    }],
    answerGuide: {
      business_question: 'Which products, categories, branches, or suppliers explain why high sales activity is not turning into strong profit?',
      source_sheets: 'Use AfriShop_Sales as the transaction table and AfriShop_Lookup for product name, category, unit cost, unit price, supplier, branch location, and manager.',
      excel_methods: 'Use VLOOKUP or XLOOKUP for product/branch attributes, calculated columns for Revenue and Profit, margin percentage, SUMIFS or PivotTables for grouped profit analysis.',
      calculation_plan: 'Revenue = QtySold x UnitPrice. Profit = QtySold x (UnitPrice - UnitCost). Margin % = Profit / Revenue. Check that product lookup and branch lookup both return values.',
      pivot_or_summary: 'Build pivots for profit by product/category/supplier/branch and a low-margin product list sorted ascending by margin.',
      evidence: 'Show total revenue, total profit, lowest margin products, top profit products, and branch/category pivot output.',
      decision: 'Recommend whether Kunta should renegotiate supplier costs, raise prices, reduce low-margin stock, or focus on higher-margin products.',
      caution: 'Profit from sales rows excludes rent, wages, waste, discounts, and stock losses, so do not present it as full business net profit.',
    },
    ruleTerms: {
      business_question: ['profit', 'products', 'suppliers'],
      source_sheets: ['afrishop_sales', 'afrishop_lookup'],
      excel_methods: ['vlookup', 'xlookup', 'pivot', 'sumifs'],
      calculation_plan: ['qtysold', 'unitprice', 'unitcost', 'margin'],
      pivot_or_summary: ['product', 'category', 'supplier', 'branch'],
      evidence: ['revenue', 'profit', 'margin'],
      decision: ['renegotiate', 'raise prices', 'supplier'],
      caution: ['rent', 'wages', 'net profit'],
    },
    criticalTerms: { calculation_plan: ['Profit'], caution: ['net profit'] },
  },
  {
    id: 'TESCO',
    title: 'Tesco: Clubcard multiplier planning',
    scenario: 'Loyalty lead Priya Chandra needs evidence before Marketing decides where to place the next Clubcard points multiplier. Use Tesco_Sales and Tesco_Lookup to enrich transactions with store region/format and customer tier, calculate points, and compare spend patterns.',
    tables: [{
      columns: ['Workbook sheets', 'Main fields', 'Business pressure'],
      rows: [['Tesco_Sales + Tesco_Lookup', 'StoreCode, CustomerID, Spend, Department, Region, Format, ClubcardTier, PointsEarned', 'Marketing wants a targeted multiplier instead of guessing from gut feel.']],
    }],
    answerGuide: {
      business_question: 'Which department, region, store format, or Clubcard tier should Marketing target with a points multiplier?',
      source_sheets: 'Use Tesco_Sales as the transaction table and Tesco_Lookup for store region/format plus customer Clubcard tier.',
      excel_methods: 'Use VLOOKUP or XLOOKUP for store attributes, INDEX/MATCH or XLOOKUP for customer tier, nested IF or IFS for PointsEarned, and PivotTables for spend/points summaries.',
      calculation_plan: 'Apply points logic by tier: Gold = Spend x 5, Silver = Spend x 3, Bronze = Spend x 1. Verify customer tier lookup before calculating points.',
      pivot_or_summary: 'Build pivots for spend and points by department, region, format, and tier; include average spend per transaction where useful.',
      evidence: 'Show the highest spend groups, highest points groups, and at least one recommendation table for the multiplier choice.',
      decision: 'Recommend the segment where a multiplier is most commercially defensible, such as a high-spend department/tier combination or an underperforming segment worth testing.',
      caution: 'Points earned reflect spend and tier rules, not campaign response; recommend a controlled offer test before assuming the multiplier will change behaviour.',
    },
    ruleTerms: {
      business_question: ['multiplier', 'department', 'tier'],
      source_sheets: ['tesco_sales', 'tesco_lookup'],
      excel_methods: ['index', 'match', 'vlookup', 'if', 'pivot'],
      calculation_plan: ['gold', 'silver', 'bronze', 'points'],
      pivot_or_summary: ['department', 'region', 'tier'],
      evidence: ['spend', 'points'],
      decision: ['recommend', 'segment', 'test'],
      caution: ['controlled', 'response', 'behaviour'],
    },
    criticalTerms: { calculation_plan: ['Gold'], excel_methods: ['INDEX'] },
  },
  {
    id: 'NHS',
    title: 'NHS: Referral waiting-time board pack',
    scenario: "Performance lead Fiona Marsh needs an honest Q1 referral picture for the Trust Board. Use NHS_Referrals and NHS_Lookup to enrich department and consultant names, calculate waiting days, compare against target weeks, and flag breaches.",
    tables: [{
      columns: ['Workbook sheets', 'Main fields', 'Business pressure'],
      rows: [['NHS_Referrals + NHS_Lookup', 'DeptCode, ConsultantID, ReferralDate, AppointmentDate, Priority, Attended, WaitingDays, TargetWeeks, BreachFlag', 'The Board needs a trustworthy picture of where waiting-time pressure is concentrated.']],
    }],
    answerGuide: {
      business_question: 'Which departments, consultants, or priority groups are breaching waiting-time targets, and how serious is the pressure?',
      source_sheets: 'Use NHS_Referrals as the case table and NHS_Lookup for department names, target wait weeks, consultant names, and specialties.',
      excel_methods: 'Use VLOOKUP or XLOOKUP for department target weeks, INDEX/MATCH or XLOOKUP for consultant names, date subtraction for WaitingDays, IF for BreachFlag, and PivotTables for breach rates.',
      calculation_plan: 'WaitingDays = AppointmentDate - ReferralDate. BreachFlag = IF(WaitingDays / 7 > TargetWeeks, "BREACH", "OK"). Check date fields are real Excel dates.',
      pivot_or_summary: 'Build pivots for breach count/rate by department, consultant, priority, and attended status; include average waiting days.',
      evidence: 'Show breach totals, breach rate, longest waits, worst department/consultant groups, and a board-ready summary table.',
      decision: 'Recommend where the Trust should investigate capacity, scheduling, or pathway issues first based on breach concentration.',
      caution: 'Referral data is sensitive and operationally complex; anonymise patient names and distinguish data quality issues from genuine performance problems.',
    },
    ruleTerms: {
      business_question: ['breach', 'waiting', 'department'],
      source_sheets: ['nhs_referrals', 'nhs_lookup'],
      excel_methods: ['vlookup', 'index', 'match', 'if', 'pivot'],
      calculation_plan: ['appointmentdate', 'referraldate', 'targetweeks'],
      pivot_or_summary: ['breach', 'department', 'consultant', 'priority'],
      evidence: ['breach rate', 'longest waits'],
      decision: ['capacity', 'scheduling', 'pathway'],
      caution: ['sensitive', 'anonymise', 'data quality'],
    },
    criticalTerms: { calculation_plan: ['WaitingDays'], caution: ['anonymise'] },
  },
  {
    id: 'POLICE',
    title: 'Police: District incident performance update',
    scenario: "Duty Inspector Callum Wren needs a district performance update that can answer the Superintendent's questions. Use Police_Incidents and Police_Lookup to enrich incidents with crime type, severity, officer and unit, calculate days open, and flag overdue open cases.",
    tables: [{
      columns: ['Workbook sheets', 'Main fields', 'Business pressure'],
      rows: [['Police_Incidents + Police_Lookup', 'CrimeCode, Area, OfficerID, Status, DateClosed, Severity, Unit, DaysOpen, OverdueFlag', 'The briefing must identify workload, overdue risk, and where management attention is needed.']],
    }],
    answerGuide: {
      business_question: 'Where are incidents concentrated, which cases are overdue, and which crime types/units need management attention?',
      source_sheets: 'Use Police_Incidents as the incident table and Police_Lookup for crime type, severity, officer name, unit, and rank.',
      excel_methods: 'Use VLOOKUP or XLOOKUP for crime attributes, INDEX/MATCH or XLOOKUP for officer/unit, IF for DaysOpen, IF plus AND for OverdueFlag, and PivotTables for area/severity/unit summaries.',
      calculation_plan: 'DaysOpen = IF(Status="Closed", DateClosed - DateReported, TODAY() - DateReported). OverdueFlag = IF(AND(Status="Open", DaysOpen > 30), "OVERDUE", "-").',
      pivot_or_summary: 'Build pivots for incidents by area, crime type, severity, unit, status, and overdue flag; include open overdue list sorted by DaysOpen.',
      evidence: 'Show overdue count, oldest open incidents, area hot spots, high-severity mix, and unit workload summary.',
      decision: 'Recommend which open overdue incidents, areas, or units need immediate review before the Superintendent meeting.',
      caution: 'Open-case age depends on TODAY(), so outputs change over time; document the refresh date and avoid identifying real people in any public portfolio version.',
    },
    ruleTerms: {
      business_question: ['overdue', 'crime', 'unit'],
      source_sheets: ['police_incidents', 'police_lookup'],
      excel_methods: ['vlookup', 'index', 'match', 'if', 'and', 'pivot'],
      calculation_plan: ['today', 'dateclosed', 'datereported', 'overdue'],
      pivot_or_summary: ['area', 'severity', 'unit', 'status'],
      evidence: ['overdue count', 'oldest', 'workload'],
      decision: ['immediate review', 'superintendent'],
      caution: ['today', 'refresh date', 'public'],
    },
    criticalTerms: { calculation_plan: ['TODAY'], caution: ['refresh date'] },
  },
];

export const EXCEL_ANALYST_CHALLENGE_CONFIG = {
  trackerId: EXCEL_ANALYST_CHALLENGE_TRACKER_ID,
  resourceKey: 'excel_analyst_challenge_5_scenarios',
  rubricKey: 'excel_real_life_analysis_scenarios',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Real-Life Excel Analyst Scenarios',
  description: 'Use the workbook scenarios to decide what Excel analysis to build, which formulas and pivots fit the business question, and what evidence would make the answer trustworthy.',
  introAlert: 'Download the workbook first. Each answer should connect Excel actions to a real decision, not just name formulas.',
  lenses: EXCEL_ANALYST_CHALLENGE_LENSES,
  workingField: {
    key: 'analysis_plan',
    label: 'Workbook analysis plan',
    placeholder: 'Example: I will enrich the sales table from the lookup sheet, calculate revenue/profit, build pivots by category and branch, then write a recommendation with one caution.',
    prompts: EXCEL_ANALYST_CHALLENGE_ITEMS.reduce((acc, item) => ({
      ...acc,
      [item.id]: `Write the Excel workflow you would build for ${item.title}. Include sheets, formulas, pivots, checks, and final output.`,
    }), {}),
  },
  items: EXCEL_ANALYST_CHALLENGE_ITEMS,
};
